import EventIcon from '@mui/icons-material/Event'
import { Alert, Button, Stack, TextField, Typography } from '@mui/material'
import { api } from '../api/client'
import type { CalendarSubscription } from '../api/generated/CalendarSubscription'
import { useAction } from '../api/useAction'
import { useApi } from '../api/useApi'

const formatDate = (date: string) =>
  new Date(date).toLocaleDateString('fr-FR', { day: 'numeric', month: 'short', year: 'numeric' })

/** The volunteer's accepted shifts in their calendar: a file to import, or a private subscription link. */
export function CalendarExport({ slug }: { slug: string }) {
  const path = `/events/${slug}/calendar`
  const subscription = useApi<CalendarSubscription | null>(path)
  const { error, pending, run } = useAction()
  const create = () =>
    run(async () => subscription.setData(await api.post<CalendarSubscription>(path, {})))
  const revoke = () =>
    run(async () => {
      await api.delete(path)
      subscription.setData(null)
    })
  const url = subscription.data?.url

  return (
    <Stack spacing={1} sx={{ alignItems: 'flex-start' }}>
      <Typography variant="subtitle1">Dans mon agenda</Typography>
      <Typography variant="body2" color="text.secondary">
        Le fichier ajoute vos créneaux validés une fois ; l'abonnement tient votre agenda à jour.
      </Typography>
      <Stack direction="row" sx={{ flexWrap: 'wrap', gap: 1 }}>
        <Button variant="outlined" startIcon={<EventIcon />} href={`/api${path}.ics`} download>
          Télécharger (.ics)
        </Button>
        {subscription.data === null && (
          <Button variant="outlined" loading={pending} onClick={create}>
            M'abonner
          </Button>
        )}
      </Stack>
      {url && (
        <>
          <TextField
            label="Lien d'abonnement"
            size="small"
            fullWidth
            value={url}
            slotProps={{ htmlInput: { readOnly: true } }}
            onFocus={(e) => (e.target as HTMLInputElement).select()}
          />
          <Stack direction="row" sx={{ flexWrap: 'wrap', gap: 1 }}>
            <Button variant="contained" href={url.replace(/^https?:/, 'webcal:')}>
              Ouvrir dans l'agenda
            </Button>
            <Button onClick={() => navigator.clipboard.writeText(url)}>Copier</Button>
          </Stack>
          <Alert severity="info">
            Lien personnel, à ne pas partager. Il ne sera plus affiché : gardez-le dans votre
            agenda.
          </Alert>
        </>
      )}
      {subscription.data && (
        <>
          {!url && (
            <Typography variant="body2">
              Abonnement créé le {formatDate(subscription.data.created_at)}.
            </Typography>
          )}
          <Stack direction="row" sx={{ flexWrap: 'wrap', gap: 1 }}>
            <Button size="small" disabled={pending} onClick={create}>
              Nouveau lien
            </Button>
            <Button size="small" color="error" disabled={pending} onClick={revoke}>
              Révoquer
            </Button>
          </Stack>
          <Typography variant="caption" color="text.secondary">
            Un nouveau lien ou la révocation arrête l'ancien lien.
          </Typography>
        </>
      )}
      {(subscription.error ?? error) && (
        <Alert severity="error">{subscription.error ?? error}</Alert>
      )}
    </Stack>
  )
}
